<template>
  <div class="virtual-table-container">
    <!-- 固定表头 -->
    <div class="virtual-table-header" :style="{ width: headerWidth + 'px' }">
      <el-table
        ref="headerTable"
        :data="[]"
        :row-key="rowKey"
        style="width: 100%; border-bottom: none;"
        :cell-style="{ borderBottom: 'none' }"
      >
        <el-table-column 
          v-if="showSelection"
          type="selection" 
          width="55"
          :reserve-selection="true"
          :selectable="() => false"
        >
          <template #header>
            <el-checkbox
              v-model="selectAllChecked"
              :indeterminate="isIndeterminate"
              @change="handleHeaderSelectAll"
            />
          </template>
        </el-table-column>
        <slot name="columns"></slot>
      </el-table>
    </div>
    
    <!-- 可滚动的内容区域 -->
    <div 
      class="virtual-table-body" 
      ref="bodyContainer"
      :style="{ 
        height: tableHeight + 'px',
        marginTop: '-1px'
      }"
      @scroll="handleScroll"
    >
      <!-- 占位元素 -->
      <div 
        class="virtual-table-placeholder" 
        :style="{ height: totalHeight + 'px' }"
      ></div>
      
      <!-- 内容表格 -->
      <div 
        class="virtual-table-content"
        :style="{ transform: `translateY(${offsetTop}px)` }"
      >
        <el-table
          ref="bodyTable"
          :data="visibleData"
          :row-key="rowKey"
          @select="handleSelect"
          @select-all="handleSelectAll"
          @selection-change="handleSelectionChange"
          :row-class-name="tableRowClassName"
          style="width: 100%; border-top: none;"
          :cell-style="{ borderTop: 'none' }"
          :show-header="false"
        >
          <el-table-column 
            v-if="showSelection"
            type="selection" 
            width="55"
            :reserve-selection="true"
          ></el-table-column>
          <slot name="columns"></slot>
        </el-table>
      </div>
    </div>
    
    <!-- 工具栏（全选/反选） -->
    <div class="virtual-table-toolbar" v-if="showToolbar">
      <div class="toolbar-left">
        <el-checkbox
          v-model="selectAllChecked"
          :indeterminate="isIndeterminate"
          @change="handleToolbarSelectAll"
        >
          全选
        </el-checkbox>
        <el-button 
          size="small" 
          @click="invertSelection"
          :disabled="tableData.length === 0"
        >
          反选
        </el-button>
        <el-button 
          size="small" 
          @click="clearAllSelection"
          :disabled="internalSelection.length === 0"
        >
          清空
        </el-button>
        <span class="selection-info">
          已选择 {{ internalSelection.length }} / {{ tableData.length }} 项
        </span>
      </div>
      <div class="toolbar-right">
        <slot name="toolbar"></slot>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'VirtualTable',
  
  props: {
    // 表格数据
    tableData: {
      type: Array,
      required: true,
      default: () => []
    },
    // 表格高度
    tableHeight: {
      type: [Number, String],
      default: 600
    },
    // 行高（需要与CSS保持一致）
    rowHeight: {
      type: Number,
      default: 48
    },
    // 行唯一标识字段
    rowKey: {
      type: String,
      default: 'id'
    },
    // 已选中的ID数组（支持v-model）
    checkedIds: {
      type: Array,
      default: () => []
    },
    // 是否显示选择列
    showSelection: {
      type: Boolean,
      default: true
    },
    // 是否显示工具栏
    showToolbar: {
      type: Boolean,
      default: true
    },
    // 预加载行数（提升滚动体验）
    bufferSize: {
      type: Number,
      default: 10
    },
  },
  
  data() {
    return {
      // 可见区域起始索引
      startIndex: 0,
      // 可见行数
      visibleCount: 0,
      // 偏移量
      offsetTop: 0,
      // 滚动容器
      scrollTop: 0,
      // 水平滚动位置
      scrollLeft: 0,
      // 内部维护的选中状态
      internalSelection: [],
      // 内部维护的选中ID集合（用于快速查找）
      internalSelectionSet: new Set(),
      // 全选状态
      selectAllChecked: false,
      // 部分选中状态
      isIndeterminate: false,
      // 表头宽度
      headerWidth: 0,
      // 用于防抖的定时器
      scrollTimer: null
    };
  },
  
  computed: {
    // 总高度
    totalHeight() {
      return this.tableData.length * this.rowHeight;
    },
    
    // 计算可见数据范围
    visibleRange() {
      const start = Math.max(0, this.startIndex - this.bufferSize);
      const end = Math.min(
        this.tableData.length,
        this.startIndex + this.visibleCount + this.bufferSize
      );
      return { start, end };
    },
    
    // 可见数据
    visibleData() {
      const { start, end } = this.visibleRange;
      return this.tableData.slice(start, end);
    },
    
    // 当前页在全部数据中的起始索引
    visibleDataStartIndex() {
      return this.visibleRange.start;
    }
  },
  
  watch: {
    // 监听数据变化，重新计算
    tableData: {
      handler() {
        this.$nextTick(() => {
          this.calculateVisibleCount();
          this.updateHeaderWidth();
          this.restoreSelection();
          this.updateSelectionState();
        });
      },
      deep: true
    },
    
    // 监听外部checkedIds变化
    checkedIds: {
      handler(newVal) {
        // 防止外部变化触发内部更新时造成的循环
        if (this.isExternalUpdate) return;
        this.syncSelectionFromExternal(newVal);
        this.updateSelectionState();
      },
      deep: true
    },
    
    // 监听表格高度变化
    tableHeight() {
      this.$nextTick(() => {
        this.calculateVisibleCount();
        this.updateHeaderWidth();
      });
    }
  },
  
  mounted() {
    this.init();
    window.addEventListener('resize', this.handleResize);
  },
  
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
    if (this.scrollTimer) {
      clearTimeout(this.scrollTimer);
    }
  },
  
  methods: {
    // 初始化
    init() {
      this.calculateVisibleCount();
      this.updateHeaderWidth();
      this.syncSelectionFromExternal(this.checkedIds);
      this.updateSelectionState();
    },
    
    // 计算可见行数
    calculateVisibleCount() {
      this.visibleCount = Math.ceil(this.tableHeight / this.rowHeight);
    },
    
    // 更新表头宽度
    updateHeaderWidth() {
      if (this.$refs.bodyContainer) {
        this.headerWidth = this.$refs.bodyContainer.clientWidth;
      }
    },
    
    // 处理滚动
    handleScroll(event) {
      if (!event.target) return;
      
      // 使用防抖优化性能
      if (this.scrollTimer) {
        clearTimeout(this.scrollTimer);
      }
      
      this.scrollTimer = setTimeout(() => {
        // 垂直滚动
        this.scrollTop = event.target.scrollTop;
        const newStartIndex = Math.floor(this.scrollTop / this.rowHeight);
        
        if (newStartIndex !== this.startIndex) {
          this.startIndex = newStartIndex;
          this.offsetTop = this.startIndex * this.rowHeight;
          this.restoreSelection();
        }
        
        // 水平滚动同步
        this.scrollLeft = event.target.scrollLeft;
        this.syncHeaderScroll();
      }, 10); // 10ms防抖
    },
    
    // 同步表头水平滚动
    syncHeaderScroll() {
      if (this.$refs.headerTable && this.$refs.headerTable.$el) {
        const headerWrapper = this.$refs.headerTable.$el.querySelector('.el-table__header-wrapper');
        if (headerWrapper) {
          headerWrapper.scrollLeft = this.scrollLeft;
        }
      }
    },
    
    // 窗口大小改变时更新宽度
    handleResize() {
      this.updateHeaderWidth();
    },
    
    // 行选择事件
    handleSelect(selection, row) {
      const rowKey = row[this.rowKey];
      const index = this.internalSelection.findIndex(item => item[this.rowKey] === rowKey);
      
      if (index > -1) {
        // 取消选中
        this.internalSelection.splice(index, 1);
        this.internalSelectionSet.delete(rowKey);
      } else {
        // 添加选中
        this.internalSelection.push(row);
        this.internalSelectionSet.add(rowKey);
      }
      
      this.emitSelectionChange();
      this.updateSelectionState();
    },
    
    // 全选事件（表格内）
    handleSelectAll(selection) {
      // 获取当前可见页的所有行
      const currentPageRows = this.tableData.slice(
        this.visibleRange.start,
        this.visibleRange.end
      );
      
      // 判断当前页是否全部选中
      const currentPageKeys = currentPageRows.map(row => row[this.rowKey]);
      const isCurrentPageAllSelected = currentPageKeys.every(key => 
        this.internalSelectionSet.has(key)
      );
      
      if (isCurrentPageAllSelected) {
        // 取消选中当前页所有行
        this.internalSelection = this.internalSelection.filter(
          row => !currentPageKeys.includes(row[this.rowKey])
        );
        currentPageKeys.forEach(key => this.internalSelectionSet.delete(key));
      } else {
        // 添加当前页所有未选中的行
        currentPageRows.forEach(row => {
          const rowKey = row[this.rowKey];
          if (!this.internalSelectionSet.has(rowKey)) {
            this.internalSelection.push(row);
            this.internalSelectionSet.add(rowKey);
          }
        });
      }
      
      this.emitSelectionChange();
      this.updateSelectionState();
    },
    
    // 表头全选事件
    handleHeaderSelectAll(checked) {
      if (checked) {
        this.selectAll();
      } else {
        this.clearAllSelection();
      }
    },
    
    // 工具栏全选事件
    handleToolbarSelectAll(checked) {
      if (checked) {
        this.selectAll();
      } else {
        this.clearAllSelection();
      }
    },
    
    // 选择变化事件
    handleSelectionChange(selection) {
      // 这个事件由el-table触发，但我们已经在handleSelect和handleSelectAll中处理了
    },
    
    // 发射选择变化事件
    emitSelectionChange() {
      const selectedIds = this.internalSelection.map(row => row[this.rowKey]);
      
      // 标记这是内部更新，避免触发watch循环
      this.isExternalUpdate = true;
      this.$nextTick(() => {
        this.$emit('update:checkedIds', selectedIds);
        this.$emit('selection-change', this.internalSelection);
        this.isExternalUpdate = false;
      });
    },
    
    // 更新选择状态（全选/部分选）
    updateSelectionState() {
      const total = this.tableData.length;
      const selectedCount = this.internalSelection.length;
      
      if (total === 0) {
        this.selectAllChecked = false;
        this.isIndeterminate = false;
        return;
      }
      
      this.selectAllChecked = selectedCount === total;
      this.isIndeterminate = selectedCount > 0 && selectedCount < total;
    },
    
    // 全选所有数据
    selectAll() {
      if (this.tableData.length === 0) return;
      
      // 直接使用tableData中的所有数据
      this.internalSelection = [...this.tableData];
      this.internalSelectionSet = new Set(this.tableData.map(row => row[this.rowKey]));
      this.emitSelectionChange();
      this.updateSelectionState();
      
      // 更新表格显示状态
      this.$nextTick(() => {
        this.restoreSelection();
      });
    },
    
    // 反选所有数据
    invertSelection() {
      if (this.tableData.length === 0) return;
      
      const newSelection = [];
      const newSelectionSet = new Set();
      
      // 遍历所有数据，选择当前未选中的行
      this.tableData.forEach(row => {
        const rowKey = row[this.rowKey];
        if (!this.internalSelectionSet.has(rowKey)) {
          newSelection.push(row);
          newSelectionSet.add(rowKey);
        }
      });
      
      this.internalSelection = newSelection;
      this.internalSelectionSet = newSelectionSet;
      this.emitSelectionChange();
      this.updateSelectionState();
      
      // 更新表格显示状态
      this.$nextTick(() => {
        this.restoreSelection();
      });
    },
    
    // 清空所有选择
    clearAllSelection() {
      if (this.$refs.bodyTable) {
        this.$refs.bodyTable.clearSelection();
      }
      this.internalSelection = [];
      this.internalSelectionSet.clear();
      this.emitSelectionChange();
      this.updateSelectionState();
    },
    
    // 从外部同步选中状态
    syncSelectionFromExternal(checkedIds) {
      if (!checkedIds || !Array.isArray(checkedIds)) return;
      
      // 清空当前选择
      this.clearSelection();
      
      // 根据ID查找对应的数据行
      const selectionMap = new Map();
      checkedIds.forEach(id => {
        const row = this.tableData.find(item => item[this.rowKey] === id);
        if (row) {
          selectionMap.set(id, row);
        }
      });
      
      // 更新内部选中状态
      this.internalSelection = Array.from(selectionMap.values());
      this.internalSelectionSet = new Set(checkedIds);
      
      // 在下一次DOM更新后恢复表格的选中状态
      this.$nextTick(() => {
        this.restoreSelection();
      });
    },
    
    // 恢复选中状态到el-table
    restoreSelection() {
      if (!this.$refs.bodyTable) return;
      
      // 清空表格当前的选择
      this.$refs.bodyTable.clearSelection();
      
      // 获取当前可见数据
      const visibleData = this.visibleData;
      
      // 设置当前可见行的选中状态
      visibleData.forEach(row => {
        const rowKey = row[this.rowKey];
        if (this.internalSelectionSet.has(rowKey)) {
          this.$refs.bodyTable.toggleRowSelection(row, true);
        }
      });
    },
    
    // 清空所有选择（内部使用）
    clearSelection() {
      if (this.$refs.bodyTable) {
        this.$refs.bodyTable.clearSelection();
      }
      this.internalSelection = [];
      this.internalSelectionSet.clear();
    },
    
    // 获取选中行数据
    getSelection() {
      return this.internalSelection;
    },
    
    // 获取选中行ID
    getSelectionIds() {
      return this.internalSelection.map(row => row[this.rowKey]);
    },
    
    // 行类名，用于调试
    tableRowClassName({ row, rowIndex }) {
      // 可以添加虚拟滚动相关的类名
      return '';
    },
    
    // 滚动到指定位置
    scrollTo(position) {
      if (this.$refs.bodyContainer) {
        this.$refs.bodyContainer.scrollTop = position;
      }
    },
    
    // 滚动到指定行
    scrollToRow(rowIndex) {
      const position = rowIndex * this.rowHeight;
      this.scrollTo(position);
    }
  }
};
</script>

<style scoped>
.virtual-table-container {
  position: relative;
  width: 100%;
  overflow: hidden;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  background-color: #fff;
}

.virtual-table-header {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 10;
  overflow: hidden;
  background: white;
  border-bottom: 1px solid #ebeef5;
}

.virtual-table-header >>> .el-table__header-wrapper {
  overflow: hidden !important;
}

.virtual-table-header >>> .el-table__body-wrapper {
  display: none !important;
}

.virtual-table-header >>> .el-table__header th {
  background-color: #f5f7fa;
  color: #909399;
  font-weight: bold;
}

.virtual-table-header >>> .el-checkbox {
  margin-left: 14px;
}

.virtual-table-body {
  position: relative;
  overflow: auto;
  margin-top: 56px; /* 表头高度，根据实际情况调整 */
  border-top: 1px solid #ebeef5;
}

.virtual-table-body::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

.virtual-table-body::-webkit-scrollbar-track {
  background: #f5f5f5;
  border-radius: 4px;
}

.virtual-table-body::-webkit-scrollbar-thumb {
  background: #c0c4cc;
  border-radius: 4px;
}

.virtual-table-body::-webkit-scrollbar-thumb:hover {
  background: #a0a4ac;
}

.virtual-table-body >>> .el-table__header-wrapper {
  display: none !important;
}

.virtual-table-placeholder {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: -1;
}

.virtual-table-content {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1;
}

/* 确保表格行高一致 */
.virtual-table-content >>> .el-table__body tr {
  height: 48px;
}

.virtual-table-content >>> .el-table__body tr:hover {
  background-color: #f5f7fa;
}

.virtual-table-content >>> .el-table__body td {
  padding: 12px 0;
}

/* 优化滚动性能 */
.virtual-table-body {
  -webkit-overflow-scrolling: touch;
  will-change: transform;
}

/* 同步列宽 */
.virtual-table-header >>> .el-table__header th,
.virtual-table-content >>> .el-table__body td {
  box-sizing: border-box;
}

/* 隐藏内容表格的边框线 */
.virtual-table-content >>> .el-table::before,
.virtual-table-content >>> .el-table::after {
  display: none;
}

/* 修复表头单元格边框 */
.virtual-table-header >>> .el-table th {
  border-bottom: none;
}

/* 修复内容单元格边框 */
.virtual-table-content >>> .el-table td {
  border-bottom: 1px solid #ebeef5;
}

.virtual-table-content >>> .el-table__body tr:last-child td {
  border-bottom: none;
}

/* 选中行样式 */
.virtual-table-content >>> .el-table__body tr.current-row > td {
  background-color: #ecf5ff;
}

/* 斑马纹效果 */
.virtual-table-content >>> .el-table--striped .el-table__body tr.el-table__row--striped td {
  background-color: #fafafa;
}

/* 工具栏样式 */
.virtual-table-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background-color: #f5f7fa;
  border-top: 1px solid #ebeef5;
  border-bottom-left-radius: 4px;
  border-bottom-right-radius: 4px;
  min-height: 56px;
  box-sizing: border-box;
}

.toolbar-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.toolbar-right {
  display: flex;
  align-items: center;
  gap: 8px;
}

.selection-info {
  margin-left: 16px;
  color: #606266;
  font-size: 14px;
  font-weight: 500;
}

/* 工具栏按钮样式 */
.virtual-table-toolbar >>> .el-button {
  min-height: 32px;
}

.virtual-table-toolbar >>> .el-checkbox {
  font-weight: 500;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .virtual-table-toolbar {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
  
  .toolbar-right {
    width: 100%;
    justify-content: flex-end;
  }
}
</style>